<template>
  <div class="flex items-center gap-0.5 rounded-md p-0.5 w-fit radio-group-container">
    <button
      v-for="option in options"
      :key="option.name"
      @click="updateValue(option.name)"
      :class="[
        {
          'radio-button-active': modelValue === option.name,
          'radio-button-inactive': modelValue !== option.name
        },
        `flex items-center justify-center rounded-md transition-all duration-200 ${option.size || 'w-8 h-8'}`
      ]"
      :aria-label="modelValue === option.name ? `${option.label} (active)` : `Switch to ${option.label}`"
      v-tooltip="{
        value: option.label,
        position: 'top',
        showDelay: 500,
        hideDelay: 0,
        class: 'custom-tooltip'
      }"
    >
      <!-- Custom SVG Icon -->
      <SvgIcon 
        v-if="option.svgIcon"
        :name="option.svgIcon"
        :width="16"
        :height="16"
        class="text-current"
      />
      <!-- PrimeVue Icon -->
      <i v-else :class="`pi ${option.iconName} ${option.iconSize || 'text-sm'}`"></i>
    </button>
  </div>
</template>

<script setup lang="ts" generic="T extends PropertyKey = string">
import SvgIcon from './SvgIcon.vue'

interface InputRadioOption<T extends PropertyKey = string> {
  name: T
  iconName?: string
  svgIcon?: string
  label: string
  size?: string
  iconSize?: string
}

interface Props<T extends PropertyKey = string> {
  modelValue: T
  options: InputRadioOption<T>[]
}

interface Emits<T extends PropertyKey = string> {
  'update:modelValue': [value: T]
}

defineProps<Props<T>>()
const emit = defineEmits<Emits<T>>()

const updateValue = (value: T) => {
  emit('update:modelValue', value)
}
</script>

<style>
/* Light mode styles */
.radio-group-container {
  background-color: var(--p-surface-100);
  border: 1px solid var(--p-border-color);
  border-radius: 8px;
  padding: 4px;
}

.radio-button-active {
  background-color: var(--p-primary-button-bg, var(--p-primary-color));
  color: var(--p-primary-color-text, #ffffff);
  border: 1px solid var(--p-primary-button-border-color, var(--p-primary-color));
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.radio-button-inactive {
  background-color: var(--p-button-bg);
  color: var(--p-button-text-color);
  border: 1px solid var(--p-button-border-color);
  transition: all 0.2s ease;
}

.radio-button-inactive:hover {
  background-color: var(--p-button-hover-bg);
  color: var(--p-button-hover-text-color);
  border-color: var(--p-button-hover-border-color);
}

/* Dark mode styles */
.dark .radio-group-container {
  background-color: var(--p-surface-800);
  border-color: var(--p-border-color);
  border-radius: 8px;
  padding: 4px;
}

.dark .radio-button-active {
  background-color: var(--p-primary-button-bg, var(--p-primary-color));
  color: var(--p-primary-button-text-color, var(--p-primary-color-text));
  border-color: var(--p-primary-button-border-color, var(--p-primary-color));
}

.dark .radio-button-inactive {
  background-color: var(--p-button-bg);
  color: var(--p-button-text-color);
  border-color: var(--p-button-border-color);
}

.dark .radio-button-inactive:hover {
  background-color: var(--p-button-hover-bg);
  color: var(--p-button-hover-text-color);
  border-color: var(--p-button-hover-border-color);
}

/* Custom tooltip styles */
:deep(.custom-tooltip) {
  background-color: var(--p-surface-overlay);
  color: var(--p-text-color);
  border: 1px solid var(--p-border-color);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.875rem;
  font-weight: 500;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06);
  z-index: 1000;
}

:deep(.custom-tooltip.p-tooltip-arrow) {
  background-color: var(--p-surface-overlay);
  border-color: var(--p-border-color);
}
</style> 